import React, { useMemo } from "react";

// Components
import { SectionCard, Row, Label, CheckboxWrapper, InputWrapper, Card, Header, Body } from "./PackingDetails.styles";
import { Wrapper, Column } from "../ProductInfo/ProductInfo.styles";
import Checkbox from "../components/Checkbox";
import { HeaderText } from "../Header/Header.styles";
import PackingDetailsTable from "./PackingDetailsTable";

// Hooks
import useProductCreateFlow from "../../../../hooks/reduxContainer/fulfillment/productCreateFlow";
import InputNumber from "../components/InputNumber";
import Input from "../components/Input";

// Interfaces
import { PackagingLevel } from "../../../../interfaces/products";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
interface Props {
  packaging_levels: PackagingLevel[];
}

const PackingDetails: React.FC<Props> = ({ packaging_levels }) => {
  const { isEditing, dispatchSetPackingDetails } = useProductCreateFlow();

  const getPackagingLevel = (level: PackagingLevel["level"]): PackagingLevel => {
    const packagingLevel = packaging_levels?.filter((packagingLevel) => packagingLevel.level === level)[0];
    return packagingLevel;
  };

  const innerPack = getPackagingLevel("inner_pack");
  const masterPack = getPackagingLevel("master_pack");
  const pallet = getPackagingLevel("pallet");

  const togglePackingLevel = (level: PackagingLevel["level"]) => {
    const newPackingDetails = packaging_levels?.map((packingLevel) =>
      packingLevel.level === level
        ? {
            ...packingLevel,
            active: !packingLevel.active,
          }
        : packingLevel
    );
    dispatchSetPackingDetails(newPackingDetails);
  };

  const updatePackingDetails = (
    level: PackagingLevel["level"],
    key: keyof PackagingLevel,
    value: PackagingLevel[keyof PackagingLevel]
  ) => {
    const newPackingDetails =
      packaging_levels?.map((packagingLevel) =>
        packagingLevel.level === level ? { ...packagingLevel, [key]: value } : packagingLevel
      ) || [];

    dispatchSetPackingDetails(newPackingDetails);
  };

  const renderPackingMethodsRow = () => {
    const renderCheckbox = (
      label: string | Function,
      handleOnChange: (checkedValue: CheckboxChangeEvent) => void,
      checked: boolean
    ) => (
      <CheckboxWrapper disabled={!isEditing}>
        <Checkbox
          label={label}
          handleOnChange={handleOnChange}
          checked={checked}
          disabled={!isEditing}
          minWidth="max-content"
          noMargin
        />
      </CheckboxWrapper>
    );

    return (
      <Row>
        <Label margin="0 16px 0 0">Your packing methods</Label>
        {renderCheckbox(
          () => (
            <Label>Inner Pack</Label>
          ),
          () => {
            togglePackingLevel("inner_pack");
          },
          innerPack.active
        )}
        {renderCheckbox(
          () => (
            <Label>Master Pack</Label>
          ),
          () => {
            togglePackingLevel("master_pack");
          },
          masterPack.active
        )}
        {renderCheckbox(
          () => (
            <Label>Pallet</Label>
          ),
          () => {
            togglePackingLevel("pallet");
          },
          pallet.active
        )}
      </Row>
    );
  };

  const renderPackagingLevelRow = (label: string, key: keyof PackagingLevel) => (
    <Row>
      <Label margin="0 16px 0 0">{label}</Label>
      {packaging_levels?.map((packagingLevel: PackagingLevel) => (
        <InputWrapper maxWidth={120} key={`${packagingLevel.level} ${key} ${packagingLevel[key]}`}>
          <InputNumber
            disabled={!isEditing || !packagingLevel.active}
            placeholder="Enter Qty"
            defaultValue={packagingLevel[key] as number | undefined}
            handleChange={(value) => updatePackingDetails(packagingLevel.level, key, Number(value) || 0)}
            precision={["length_value", "width_value", "height_value", "base_product_quantity"].includes(key) ? 0 : 1}
          />
        </InputWrapper>
      ))}
    </Row>
  );

  const renderDefineSkuNameRow = () => (
    <Row>
      <Label margin="0 16px 0 0">Define SKU Name</Label>
      {packaging_levels?.map((packingLevel) => (
        <InputWrapper maxWidth={120} key={`${packingLevel.level} sku`}>
          <Input
            disabled={!isEditing || !packingLevel.active}
            defaultValue={packingLevel.sku}
            handleChange={(value) => updatePackingDetails(packingLevel.level, "sku", value)}
          />
        </InputWrapper>
      ))}
    </Row>
  );

  const renderDescriptionRow = () => (
    <Row>
      <Label margin="0 16px 0 0">Description</Label>
      {packaging_levels?.map((packingLevel) => (
        <InputWrapper maxWidth={120} key={`${packingLevel.level} description`}>
          <Input
            textarea
            minHeight={120}
            disabled={!isEditing || !packingLevel.active}
            defaultValue={packingLevel.description}
            placeholder="Optional"
            handleChange={(value) => updatePackingDetails(packingLevel.level, "description", value)}
          />
        </InputWrapper>
      ))}
    </Row>
  );

  const renderDescriptionCard = () => {
    return (
      <Card>
        <Header>What are packing levels?</Header>
        <Body>Packing levels describe how your product will be packaged before it is shipped to a warehouse.</Body>
        <Body>
          Inner packs contain more than one of the same product (e.g. an inner pack could be a 6-pack of soda). Multiple
          inner packs can be packaged into a master pack. Master packs are generally the largest packaging level stacked
          on a pallet.
        </Body>
        <Body>
          Master packs contain more than one of the same product, either inner packs or individual items (e.g. a master
          case could be 4x 6-packs of soda, or 24 individual sodas).
        </Body>
        <Body>
          Pallets contain stacked master packs that are placed on a wooden frame (e.g. a pallet could contain 40 master
          packs of soda).
        </Body>
      </Card>
    );
  };

  const renderPackingDetails = () => (
    <Wrapper>
      <HeaderText margin="0 0 16px">PACKING DETAILS (OPTIONAL)</HeaderText>
      <SectionCard>
        <Column margin="0 56px 0 0">
          {isEditing ? (
            <>
              {renderPackingMethodsRow()}
              {renderPackagingLevelRow("Base Products Per Package", "base_product_quantity")}
              {renderDefineSkuNameRow()}
              {renderDescriptionRow()}
              {renderPackagingLevelRow("Length (in)", "length_value")}
              {renderPackagingLevelRow("Width (in)", "width_value")}
              {renderPackagingLevelRow("Height (in)", "height_value")}
              {renderPackagingLevelRow("Weight (lb)", "weight_value")}
            </>
          ) : (
            <PackingDetailsTable packagingLevels={packaging_levels} />
          )}
        </Column>
        <Column>{renderDescriptionCard()}</Column>
      </SectionCard>
    </Wrapper>
  );

  return useMemo(renderPackingDetails, [isEditing, packaging_levels]);
};

export default PackingDetails;
